import { Box, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { LoadingButton } from "@mui/lab";
import CustomAlert from "../../events/components/CustomAlert";
import { setAllMediaFilesArray } from "../../../redux/mediaFilesSlice";

const MAX_VISIBLE_ROWS = 4;

// Fixed row height per breakpoint (matches the row's font size), so the
// list's height can be computed exactly as rowHeight * rowCount at every
// resolution, without needing to measure the DOM.
const ROW_HEIGHT_PX = { xs: 34, sm: 36, md: 38, lg: 42, xl: 56 };

// Margin the popup keeps from every viewport edge, at any screen size.
const VIEWPORT_MARGIN_PX = 32;

const FileListSection = ({ title, description, files, tagLabel, tagColor }) => {
    const visibleRows = Math.min(files.length, MAX_VISIBLE_ROWS) || 1;
    const listHeight = Object.fromEntries(
        Object.entries(ROW_HEIGHT_PX).map(([bp, rowPx]) => [bp, `${rowPx * visibleRows}px`])
    );

    return (
        <Box mb={{ xs: 2, md: 3 }} sx={{ width: '100%' }}>
            <Typography sx={{ fontFamily: 'Rubik', fontSize: { xs: '12px', sm: '13px', md: '13px', lg: '14px', xl: '19px' }, color: 'black', fontWeight: 600, mb: 0.5 }}>
                {title}
            </Typography>
            <Typography sx={{ fontFamily: 'Rubik', fontSize: { xs: '11px', sm: '12px', md: '12px', lg: '13px', xl: '18px' }, color: '#666666', mb: 1 }}>
                {description}
            </Typography>

            <Box
                sx={{
                    width: '100%',
                    height: listHeight,
                    flexShrink: 0,
                    overflowY: 'auto',
                    overflowX: 'auto',
                    border: '1px solid #E9EBE9',
                    borderRadius: '5px',
                    px: 2,
                }}
            >
                {files.map((file, index) => (
                    <Box
                        key={file.id ?? index}
                        className="flex row j-between a-center"
                        sx={{
                            height: { xs: `${ROW_HEIGHT_PX.xs}px`, sm: `${ROW_HEIGHT_PX.sm}px`, md: `${ROW_HEIGHT_PX.md}px`, lg: `${ROW_HEIGHT_PX.lg}px`, xl: `${ROW_HEIGHT_PX.xl}px` },
                            width: 'fit-content',
                            minWidth: '100%',
                            boxSizing: 'border-box',
                            gap: 2,
                            borderBottom: index !== files.length - 1 ? '1px solid #F0F0F0' : 'none',
                        }}
                    >
                        <Typography sx={{ fontFamily: 'Rubik', fontSize: { xs: '11px', sm: '12px', md: '12px', lg: '13px', xl: '18px' }, color: 'black', whiteSpace: 'nowrap', flexShrink: 0 }}>
                            {file.name}
                        </Typography>
                        <Typography sx={{ fontFamily: 'Rubik', fontSize: { xs: '10px', sm: '10px', md: '11px', lg: '12px', xl: '16px' }, color: tagColor, fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>
                            {tagLabel}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

const MissingFilesPopup = () => {
    const dispatch = useDispatch()
    const mediaFilesReducer = useSelector(state => state.mediaFilesReducer)
    const missingFiles = mediaFilesReducer.missing_files || []
    const duplicateFiles = mediaFilesReducer.duplicate_files || []

    const hasMissing = missingFiles.length > 0;
    const hasDuplicates = duplicateFiles.length > 0;

    const handleClose = () => {
        dispatch(setAllMediaFilesArray({ missing_files_popup: false }))
    };

    const headingParts = [];
    if (hasMissing) headingParts.push(`${missingFiles.length} ${missingFiles.length === 1 ? 'file' : 'files'} couldn't be added`);
    if (hasDuplicates) headingParts.push(`${duplicateFiles.length} duplicate ${duplicateFiles.length === 1 ? 'file' : 'files'} found`);

    return (
        <CustomAlert
            open={mediaFilesReducer.missing_files_popup}
            handleClose={handleClose}
            PaperProps={{
                sx: {
                    borderRadius: '5px',
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                    margin: `${VIEWPORT_MARGIN_PX}px`,
                    maxHeight: `calc(100vh - ${VIEWPORT_MARGIN_PX * 2}px)`,
                    maxWidth: `calc(100vw - ${VIEWPORT_MARGIN_PX * 2}px)`,
                    width: { xs: '92vw', sm: '460px', md: '500px', lg: '540px', xl: '680px' },
                    height: 'auto',
                },
            }}
        >
            <Box
                p={5}
                className="flex col"
                sx={{
                    backgroundColor: 'white',
                    borderRadius: '5px',
                    minWidth: 0,
                    overflowX: 'hidden',
                    overflowY: 'auto',
                    boxSizing: 'border-box',
                    maxHeight: `calc(100vh - ${VIEWPORT_MARGIN_PX * 2}px)`,
                }}
            >

                <Box mb={{ xs: 2, md: 3 }} textAlign={'center'}>
                    <Typography variant="h4" sx={{ fontFamily: 'Rubik', fontSize: { xs: '14px', sm: '15px', md: '16px', lg: '18px', xl: '24px' }, color: 'black', fontWeight: 600 }}>
                        {headingParts.join(' and ')}
                    </Typography>
                    <Typography sx={{ fontFamily: 'Rubik', fontSize: { xs: '11px', sm: '12px', md: '12px', lg: '13px', xl: '18px' }, color: '#666666', mt: 1 }}>
                        The rest of your files are still zipping and will download as usual.
                    </Typography>
                </Box>

                {hasMissing && (
                    <FileListSection
                        title={`${missingFiles.length === 1 ? 'File' : 'Files'} skipped from your download`}
                        description={`The following ${missingFiles.length === 1 ? 'file was' : 'files were'} unavailable and could not be included:`}
                        files={missingFiles}
                        tagLabel="Unavailable"
                        tagColor="#c0392b"
                    />
                )}

                {hasDuplicates && (
                    <FileListSection
                        title={`Duplicate ${duplicateFiles.length === 1 ? 'file' : 'files'} detected`}
                        description={`The following ${duplicateFiles.length === 1 ? 'file appears' : 'files appear'} more than once in your selection. Only one copy of each will be included in your download:`}
                        files={duplicateFiles}
                        tagLabel="Duplicate"
                        tagColor="#b8860b"
                    />
                )}

                <Box sx={{ display: 'flex', justifyContent: 'center' }} gap={2} mt={{ xs: 1, md: 1 }}>
                    <LoadingButton
                        size="small"
                        onClick={handleClose}
                        color="secondary"
                        style={{ borderRadius: '5px', textTransform: 'none' }}
                        sx={{ fontSize: { xs: '12px', sm: '12px', md: '14px', lg: '16px', xl: '20px' }, fontWeight: 700, width: { xs: '76px', sm: '76px', md: '76px', lg: '86px', xl: '129px' }, minWidth: '110px !important', color: 'black' }}
                        variant="contained"
                    >
                        Got it
                    </LoadingButton>
                </Box>
            </Box>
        </CustomAlert>
    )
}

export default MissingFilesPopup
